// src/components/Layout/navVisibility.ts — who may SEE a navigation entry.
//
// Until now the nav asked one question: what is your role? That is the wrong
// question on its own, because the same six role names are issued inside both
// kinds of organisation. `Organization.type` is 'service' (a provider that runs
// the platform: Green Giraffe, CatchMyCall, Teeoff, XtremeGenAI) or 'business'
// (a tenant that runs a business ON the platform — ~40 of them today). A
// super_admin of a BUSINESS is an admin of that business, not an operator of
// the platform, yet a role-only gate hands them Infrastructure, WABA
// Registrations and provider billing.
//
// So the gate is role AND type, never type alone:
//
//   role  decides WHICH of your organisation's tools you get
//   type  decides WHOSE tools they are
//
// Dropping the role half would show a business's operator_user the admin
// entries; dropping the type half is today's leak. Both must pass.
//
// ── This is navigation hygiene, not access control ─────────────────────────
// Hiding a link does not protect anything. Most routes in App.tsx pass no
// `roles` at all, and even those that do are checked in the browser, where the
// user controls the code. The only real boundary is the backend's
// org-scoping/hasRole checks. What this module buys is that a tenant is not
// shown, and cannot casually wander into, machinery that is not theirs.

/** 'service' = runs the platform. 'business' = runs a business on it. */
import { ACTIVE_BRAND, BrandKey } from 'constants/branding';

export type OrgKind = 'service' | 'business' | 'unknown';

export interface NavPrincipal {
  /** The role held IN the current organisation (case-insensitive). */
  role: string;
  kind: OrgKind;
  /**
   * What this organisation is entitled to, from GET /v1/feature-locks/effective.
   * Undefined until it loads, or if the call failed — see the note on
   * featureAllows() for why that deliberately changes nothing.
   */
  features?: EffectiveFeatureMap;
  /**
   * Allow-listed features this organisation holds, from GET /v1/feature-grants/mine.
   * Undefined while unknown. See grantAllows() for how that is treated.
   */
  grants?: string[];
  /**
   * Whether this user may have a browser softphone in this organisation, from
   * GET /telephony/softphone/entitlement (purchased seats only). Undefined
   * until it loads or if it failed — which, as with `features`, changes
   * nothing: only an explicit `false` hides the Softphone entry.
   */
  softphoneEntitled?: boolean;
}

/**
 * Structural, not imported from the service, so this module stays free of API
 * types and remains directly testable.
 */
export type EffectiveFeatureMap = Record<string, { allowed?: boolean; readable?: boolean } | undefined>;

/**
 * The organisation's kind, whatever spelling arrived.
 *
 * Both spellings are real and neither is a typo:
 *   - POST /auth/login lowercases the enum
 *     (AuthService.convertToUserDTO: org.getType().name().toLowerCase()),
 *     and `currentOrganization` comes from that payload — so 'business'.
 *   - GET /organizations/businesses serialises the enum raw — so 'SERVICE'.
 * A case-sensitive compare would classify half the app's own responses wrong;
 * OrganizationSwitcher's badge already does exactly that.
 */
export function resolveOrgKind(org?: { type?: string | null } | null): OrgKind {
  const t = (org?.type ?? '').trim().toLowerCase();
  if (t === 'business') return 'business';
  if (t === 'service') return 'service';
  return 'unknown';
}

/**
 * Sections that exist to operate the platform. A BUSINESS never sees these,
 * whatever role it holds.
 *
 * 'billing' is here and it is a judgement call worth stating: the Billing pages
 * are not "your invoices". CreditAccounts creates an account against a typed-in
 * organizationId, ServiceRates sets per-organization pricing, and the dashboard
 * ranks top consumers across organisations. That is a provider's ledger of its
 * tenants, so it goes with the platform. A tenant-facing "my balance" screen
 * would be a different page, and does not exist yet.
 *
 * 'ops_tools' (Live Calls) is NOT here any more: since 14 Oct 2026 the server
 * scopes live calls to the acting organisation's tree, so a business admin sees
 * its own calls there and the page belongs to tenants as much as to the platform.
 * Everything else that used to sit under that label (Agent Console, Softphone,
 * Place a Call, Team Directory, Extensions, Ring Groups) is in Contact Center.
 */
export const PLATFORM_SECTION_IDS: readonly string[] = [
  'infrastructure',    // trunks, providers, capacity, workers, system health
  'platform_whatsapp', // the platform's Meta app config + WABA registrations
  'service_controls',  // suspending organisations
  'billing',           // the provider's ledger of its tenants
];

/**
 * Platform destinations that do NOT live in a platform section, so a section
 * gate alone would miss them. Matched as path prefixes on a real boundary
 * (exact, or followed by '/'), the same rule the sidebar uses for highlighting,
 * so '/billing' also covers '/billing/invoices' and '/telephony/workers' covers
 * '/telephony/workers/config'.
 */
export const PLATFORM_PATHS: readonly string[] = [
  // Administration zone, but provider-side:
  '/revenue',                  // top customers, churn, ARPU — the provider's book
  '/businesses',               // creating tenants under a provider
  '/billing',                  // see the note on the 'billing' section above
  // Workspace zone, but platform plumbing:
  //   Call Data Sources holds host/user/password for an external switch's
  //   database and its discovery step lists every business on that switch.
  //   That is federation plumbing an operator sets up, not a tenant setting.
  '/telephony/data-sources',
  //   Voice AI Integrations is where an organisation's agent RUNTIME lives —
  //   the voicex base URL, the OAuth client id/secret, the websocket template
  //   and the binding to a voicex business. A service provider sets that up
  //   once and its businesses inherit it; a tenant editing it would repoint or
  //   orphan every agent already pushed under the provider's business id.
  //   The backend already refuses business roles here (see
  //   OrganizationAiProviderController: "Business owners/admins are
  //   deliberately excluded"), so the link was showing a tenant a door that
  //   only ever answers 403. Businesses keep /crm/integrations and
  //   /crm/booking-setup — their own bookings and CRM are theirs to configure.
  '/agents/voice-ai-integrations',
  // The platform sections' own paths, repeated so a typed URL is caught even
  // though the section gate already hides the links:
  '/telephony/overview',
  '/telephony/providers',
  '/telephony/capacity-limits',
  '/telephony/workers',
  '/system-health',
  '/platform',                 // /platform/numbers, /platform/service-controls
  '/whatsapp/embedded-signup-config',
  '/whatsapp/waba-registrations',
];

/**
 * Nav path -> the feature that entitles it. Longest match wins, so a specific
 * entry beats the broader one it sits inside.
 *
 * These name the SAME features the backend gates on (LockedFeature.java). That
 * is the point of the exercise: the sidebar and the API answer from one
 * computation, so a business is never shown a page whose every request 403s.
 *
 * A path absent from this table is not feature-gated at all — Contacts, the
 * dashboard, settings. Silence here means "always available", never "unknown".
 */
export const FEATURE_PATHS: ReadonlyArray<readonly [string, string]> = [
  ['/whatsapp', 'WHATSAPP'],
  ['/whatsapp/campaigns', 'WHATSAPP_CAMPAIGNS'],
  ['/whatsapp/chats', 'WHATSAPP_MESSAGING'],
  ['/whatsapp/templates', 'WHATSAPP_MESSAGING'],

  ['/agents', 'AI_AGENTS'],
  ['/agents/voice-ai-integrations', 'AI_AGENTS'],

  ['/crm/integrations', 'CRM_INTEGRATIONS'],
  ['/crm/custom-api-deliveries', 'CRM_INTEGRATIONS'],
  ['/crm/booking-setup', 'BOOKING'],
  ['/crm/captures', 'BOOKING'],
  // Locked with the list it summarises. A summary of rows somebody is not
  // allowed to see is still those rows, in aggregate.
  ['/crm/lead-overview', 'BOOKING'],

  ['/campaigns', 'VOICE_AI_CAMPAIGNS'],
  ['/voice-campaigns', 'VOICE_AI_CAMPAIGNS'],

  ['/telephony/softphone', 'SOFTPHONE'],
  ['/telephony/extensions', 'SOFTPHONE'],
  ['/telephony/ring-groups', 'SOFTPHONE'],
  ['/telephony/directory', 'SOFTPHONE'],
  // '/telephony/console' — the ACTUAL route. This read '/telephony/agent-console',
  // which is not a path this app has, so the gate matched nothing and Agent Console
  // was never feature-gated at all.
  ['/telephony/console', 'SOFTPHONE'],

  // '/telephony/place-call' WAS listed here and is not a route either — the page is
  // '/telephony/outbound'. It is deliberately NOT re-pointed.
  //
  // Placing a call now has two routes: an extension, which does need outbound calling
  // enabled, and an AI agent, which does not — it carries the agent's socket and needs
  // no trunk capability, no seat and no softphone. Gating the page on OUTBOUND_CALLING
  // would hide it from exactly the tenants the AI route exists for: CMC India Health
  // runs outbound agents with no seats and no outbound-calling flag.
  //
  // The server refuses per-route, which is where the distinction can actually be
  // made. Hiding the whole page cannot make it. A test pins this.
  ['/telephony/outbound-routes', 'OUTBOUND_CALLING'],

  ['/telephony/providers', 'VOICE_PROVIDERS'],
  ['/telephony/trunks', 'VOICE_PROVIDERS'],
  ['/telephony/capacity-limits', 'VOICE_PROVIDERS'],
  ['/platform/numbers', 'VOICE_PROVIDERS'],

  ['/sms', 'SMS'],
  ['/rcs', 'RCS'],
];

/** The feature entitling this path, or null when it is not feature-gated. */
export function featureFor(path: string): string | null {
  const p = (path || '').toLowerCase().replace(/\/+$/, '') || '/';
  let best: string | null = null;
  let bestLen = -1;
  for (const [prefix, feature] of FEATURE_PATHS) {
    if ((p === prefix || p.startsWith(prefix + '/')) && prefix.length > bestLen) {
      best = feature;
      bestLen = prefix.length;
    }
  }
  return best;
}

/**
 * The entitlement half of the gate.
 *
 * Visibility keys off `readable`, not `allowed`, and the difference is the
 * whole reason LockMode exists. BLOCK_SEND is a suspension that leaves the
 * customer their history, reports and support visibility — hiding those pages
 * would make a billing hold look like data loss. BLOCK_ALL is the hard cutoff,
 * and only then does the feature disappear.
 *
 * An unknown answer — still loading, or the call failed — allows. Same
 * reasoning as kindAllowsPlatform: showing a label reveals nothing, the backend
 * still refuses the data, and hiding on unknown would empty the nav for
 * everyone the instant that one request fails. Fail open on labels, fail closed
 * on data.
 */
export function featureAllows(path: string, p: NavPrincipal): boolean {
  if (!p.features) return true;
  const feature = featureFor(path);
  if (!feature) return true;
  const access = p.features[feature];
  if (!access || access.readable === undefined) return true;
  return access.readable;
}

/**
 * Paths that exist only for organisations the platform has GRANTED a feature to
 * (organization_feature_grants) — the opposite polarity to FEATURE_PATHS, which
 * are on until locked. Mirrors GrantedFeature.java.
 *
 * Embedded Signup config is master-only by default (user decision 14 Oct 2026):
 * there is one Meta app config, Green Giraffe's, which every org inherits, and a
 * provider-level copy shadows it and goes stale. A provider sees the page only
 * once master grants it. Businesses keep onboarding through WhatsApp Setup, which
 * reads the inherited config and is not listed here.
 *
 * Worker Config is the same (14 Oct 2026, "add provider, capacity, worker — only
 * allowed once allowed"): worker settings are platform-wide switch runtime config
 * with nothing per-organisation in them, so a provider neither sees nor reads the
 * page until granted WORKER_CONFIG. Providers and Capacity Limits are NOT listed:
 * an ungranted provider keeps a read-only view of its own rows there, and those
 * pages hide their own add/edit/delete controls (ADD_PROVIDER / CAPACITY_MANAGE).
 */
export const GRANT_PATHS: ReadonlyArray<readonly [string, string]> = [
  ['/whatsapp/embedded-signup-config', 'WHATSAPP_EMBEDDED_SIGNUP_CONFIG'],
  ['/telephony/workers', 'WORKER_CONFIG'],
];

/** Roles that hold every grant regardless (the server's MASTER_ADMIN/PLATFORM_ADMIN). */
const GRANT_EXEMPT_ROLES = ['master_admin', 'platform_admin'];

/** The grant this path needs, or null when it needs none. */
export function grantFor(path: string): string | null {
  const p = (path || '').toLowerCase().replace(/\/+$/, '') || '/';
  const hit = GRANT_PATHS.find(([prefix]) => p === prefix || p.startsWith(prefix + '/'));
  return hit ? hit[1] : null;
}

/**
 * The allow-list half of the gate.
 *
 * `failOpen` decides what an UNKNOWN grant list (still loading, or the call
 * failed) means. The link fails CLOSED — the default for these pages is hidden,
 * so showing one to a provider for the instant before the answer arrives would
 * be exactly the wrong flash. The route fails OPEN — otherwise a granted
 * provider opening the URL directly is bounced to / before their grants load;
 * PrivateRoute re-checks once they do, and the API refuses regardless.
 */
export function grantAllows(path: string, p: NavPrincipal, failOpen: boolean): boolean {
  const grant = grantFor(path);
  if (!grant) return true;
  if (GRANT_EXEMPT_ROLES.includes(p.role)) return true;
  if (!p.grants) return failOpen;
  return p.grants.includes(grant);
}

const onBoundary = (path: string, prefix: string) =>
  path === prefix || path.startsWith(prefix + '/');

/** Whether this path is platform machinery. Prefix match on a path boundary. */
export function isPlatformPath(path: string): boolean {
  const p = (path || '').toLowerCase().replace(/\/+$/, '') || '/';
  return PLATFORM_PATHS.some(prefix => onBoundary(p, prefix));
}

export function isPlatformSection(id: string): boolean {
  return PLATFORM_SECTION_IDS.includes(id);
}

const roleAllows = (roles: string[] | undefined, role: string) =>
  !roles || roles.some(r => r.toLowerCase() === role);

/**
 * The type half of the gate.
 *
 * An UNKNOWN kind is treated as not-a-business, i.e. it changes nothing. This
 * is deliberate and it is the safer of the two mistakes:
 *
 *  - Every real login carries a type: AuthService always sets it, and the
 *    synthetic-org fallback in AuthContext derives it from user.user_type. So
 *    'unknown' means a payload shape we do not recognise, not "a business whose
 *    type is still loading" — and the sidebar does not even mount until
 *    PrivateRoute's `loading` is false.
 *  - Hiding on unknown would empty the nav of the people who run the platform
 *    the moment any auth payload drifts, and an operator staring at a nav with
 *    no Infrastructure has no way to tell "hidden" from "broken".
 *  - Showing on unknown reveals link labels, nothing more; the backend still
 *    refuses the data. Fail open on labels, fail closed on data.
 */
const kindAllowsPlatform = (kind: OrgKind) => kind !== 'business';

/** May this principal see a section header (and therefore its contents)? */
/**
 * Sections switched off across every portal until they are finished.
 *
 * These are whole areas of the product that are not built out yet, so they are
 * hidden rather than shipped half-done. This is a build-time list, not a
 * per-tenant permission: the sections do not work anywhere, so there is nothing
 * for a tenant to be granted.
 *
 * Nothing is deleted. Remove an id here and the section, its nav entry and its
 * routes all come back exactly as they were.
 *
 * REACT_APP_ENABLE_SECTIONS re-enables them for local work, comma separated,
 * e.g. REACT_APP_ENABLE_SECTIONS=developer,automations
 *
 * ── `automations` shipped on 2026-09-10 ───────────────────────────────────
 * It was off because the section as a whole was not built out. Two of its
 * entries now are, and are in use: Reply Rules place real calls off a WhatsApp
 * reply, and the calling window those obey is Business Hours. Leaving the
 * section hidden made a shipped feature unreachable except by typing its URL.
 *
 * The rest of the section comes back with it, which is the documented
 * behaviour of removing an id here — Transfer Profiles and Lead Routing are a
 * tenant's own tools, and navVisibility.test.ts already asserted they must be
 * visible to a business admin once the section ships.
 */
const DISABLED_SECTION_IDS: readonly string[] = [];

/**
 * ── `developer` shipped on 2026-09-17 ─────────────────────────────────────
 * Same story as `automations` above, and it hid a feature built specifically
 * to be self-service. Webhook Stubs lets a tenant define a response and get a
 * URL that returns it; its nav entry was opened to EVERYONE on 2026-09-16, and
 * that changed nothing, because this list is checked ABOVE the role gate and
 * takes the section's routes with it. The page was in the shipped bundle and
 * unreachable by every user, including the one who asked for it.
 *
 * The rest of the section comes back with it, which is the documented
 * behaviour of removing an id. That is correct here rather than merely
 * tolerated: API Keys and Webhooks are backed end-to-end by
 * DeveloperController (/developer/api-keys, /developer/webhooks and their
 * stats, rotate, enable/disable and delete routes), so the "not built out yet"
 * reason above had gone stale. They stay gated to DEVELOPERS and the API
 * Tester to admins, so a business user opening this section sees Webhook Stubs
 * alone — which is what the 2026-09-16 change intended.
 *
 * The list is now empty and that is a valid state: nothing is unfinished
 * enough to hide. The override and route-guard machinery is still exercised by
 * PORTAL_DISABLED_SECTION_IDS below, which is where the remaining switched-off
 * sections live.
 */

/**
 * Sections switched off on SOME portals only.
 *
 * Unlike the list above, these work — they are simply not part of what a given
 * portal sells. The AI Agents section, and the CRM surfaces that belong to it,
 * are a cpass product; the TVOICE portals do not offer them.
 *
 * Keyed by the portal identity that already decides the brand, so a portal
 * names itself once and both its name and its product follow.
 */
const PORTAL_DISABLED_SECTION_IDS: Record<BrandKey, readonly string[]> = {
  gg: [],
  tvoice: ['agents'],
  // CatchMyCall sells the AI agent product, so nothing is switched off at the
  // PORTAL level. What its customers see is narrowed per reseller instead, by
  // the portal profile — a different mechanism with a different owner: this map
  // is ours and compiled in, that one is theirs and configurable.
  cmc: [],
};

/**
 * The URLs each disabled section owns. Needed separately because hiding a link
 * leaves its address typeable and a bookmark to it still resolves — the same
 * reason canOpenPath exists at all.
 */
const DISABLED_SECTION_PATHS: Readonly<Record<string, readonly string[]>> = {
  developer: ['/developer'],
  // The Agents section owns the CRM surfaces too — Booking & CRM, Booking
  // Setup, Leads & Bookings and Call Activity all live under it, so they go
  // with it rather than being stranded behind a hidden parent.
  agents: ['/agents', '/crm'],
};

const enabledByEnv = (): Set<string> =>
  new Set(
    (process.env.REACT_APP_ENABLE_SECTIONS || '')
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean),
  );

/** Every section switched off for a given portal: the global list plus its own. */
const disabledIdsFor = (portal: BrandKey): readonly string[] => [
  ...DISABLED_SECTION_IDS,
  ...(PORTAL_DISABLED_SECTION_IDS[portal] || []),
];

/** Is this whole section switched off on this portal? */
export function isSectionDisabled(
  sectionId: string,
  enabled: Set<string> = enabledByEnv(),
  portal: BrandKey = ACTIVE_BRAND,
): boolean {
  const id = (sectionId || '').toLowerCase();
  return disabledIdsFor(portal).includes(id) && !enabled.has(id);
}

/**
 * Does this URL belong to a switched-off section?
 *
 * Matched on a path SEGMENT boundary, so '/developer' catches
 * '/developer/api-keys' without also catching a future '/developer-tools'.
 */
export function isDisabledSectionPath(
  path: string,
  enabled: Set<string> = enabledByEnv(),
  portal: BrandKey = ACTIVE_BRAND,
): boolean {
  const candidate = (path || '').toLowerCase();
  return disabledIdsFor(portal).some((id) => {
    if (enabled.has(id)) return false;
    return (DISABLED_SECTION_PATHS[id] || []).some(
      (prefix) => candidate === prefix || candidate.startsWith(prefix + '/'),
    );
  });
}

export function canSeeSection(
  section: { id: string; roles?: string[] },
  p: NavPrincipal,
): boolean {
  if (isSectionDisabled(section.id)) return false;
  if (!roleAllows(section.roles, p.role)) return false;
  if (isPlatformSection(section.id)) return kindAllowsPlatform(p.kind);
  return true;
}

/** The Softphone page — the one entry gated on purchased seats. */
export const SOFTPHONE_PATH = '/telephony/softphone';

/**
 * The seat half of the Softphone gate. Applied to the NAV ENTRY only, not to
 * canOpenPath: someone who reaches the page anyway (a bookmark, a link from
 * Agent Console) should be told WHY there is no phone, which the page does,
 * rather than bounced somewhere silent.
 */
export function softphoneAllows(path: string, p: NavPrincipal): boolean {
  const q = (path || '').toLowerCase().replace(/\/+$/, '');
  if (!onBoundary(q, SOFTPHONE_PATH)) return true;
  return p.softphoneEntitled !== false;
}

/**
 * Single pages that ship dark: hidden everywhere unless the build names the flag in
 * REACT_APP_ENABLE_FEATURES (comma separated), e.g. REACT_APP_ENABLE_FEATURES=whatsapp-ads.
 *
 * For a page whose BACKEND is switched off platform-wide — WhatsApp Ads waits on Meta's App
 * Review of the ads permissions (cpass.whatsapp.ads.enabled=false). Showing it before then
 * offers every manager a page that can only say "not yet". Once the flag is in the build, the
 * page still asks the server (/v1/whatsapp/ads/status) and explains if the backend is off.
 */
export const FLAGGED_PATHS: ReadonlyArray<readonly [string, string]> = [
  ['/whatsapp/ads', 'whatsapp-ads'],
  // TRAI TCCCPR compliance screens. The backend is shadow-only and unreleased; a normal build
  // shows none of it — not the section, not the category fields on the campaign/agent forms.
  ['/compliance', 'trai-compliance'],
  // Workflow canvas (Phase 2). The backend ships off too (cpass.workflows.enabled); with the
  // flag in the build the list page still says so when the server answers 404.
  ['/workflows', 'workflows'],
];

const featuresByEnv = (): Set<string> =>
  new Set(
    (process.env.REACT_APP_ENABLE_FEATURES || '')
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean),
  );

/**
 * Is a build flag set? For a PANEL that ships dark inside a page that is already live (no path
 * of its own to list in FLAGGED_PATHS), e.g. REACT_APP_ENABLE_FEATURES=refusal-counts.
 */
export function isFeatureEnabled(flag: string, enabled: Set<string> = featuresByEnv()): boolean {
  return enabled.has(flag.trim().toLowerCase());
}

/** Is a build feature flag (REACT_APP_ENABLE_FEATURES) on? For controls inside shared pages. */
export function isBuildFeatureOn(flag: string, enabled: Set<string> = featuresByEnv()): boolean {
  return enabled.has(flag.toLowerCase());
}

/** Is this path behind a build flag that is not set? */
export function isFlaggedOff(path: string, enabled: Set<string> = featuresByEnv()): boolean {
  const p = (path || '').toLowerCase().replace(/\/+$/, '');
  const hit = FLAGGED_PATHS.find(([prefix]) => onBoundary(p, prefix));
  return !!hit && !enabled.has(hit[1]);
}

/** May this principal see a single nav entry? */
export function canSeeItem(
  item: { path: string; roles?: string[] },
  p: NavPrincipal,
): boolean {
  if (isDisabledSectionPath(item.path)) return false;
  if (isFlaggedOff(item.path)) return false;
  if (!roleAllows(item.roles, p.role)) return false;
  if (!grantAllows(item.path, p, false)) return false;
  if (isPlatformPath(item.path)) return kindAllowsPlatform(p.kind);
  if (!softphoneAllows(item.path, p)) return false;
  return featureAllows(item.path, p);
}

/**
 * May this principal open this URL? Used by PrivateRoute so that hiding a link
 * is not undone by typing its address. Again: this bounces a wrong turn, it
 * does not defend the data behind the route.
 */
export function canOpenPath(path: string, p: NavPrincipal): boolean {
  if (isDisabledSectionPath(path)) return false;
  if (isFlaggedOff(path)) return false;
  if (!grantAllows(path, p, true)) return false;
  if (isPlatformPath(path)) return kindAllowsPlatform(p.kind);
  return featureAllows(path, p);
}

/** Builds the principal from what useAuth() hands the nav. */
export function navPrincipal(
  currentOrganization?: { type?: string | null; role?: string | null } | null,
  user?: { role?: string | null; user_type?: string | null } | null,
  features?: EffectiveFeatureMap,
  grants?: string[],
  softphoneEntitled?: boolean,
): NavPrincipal {
  const role = (currentOrganization?.role || user?.role || '').toLowerCase();
  // user.user_type is the same vocabulary as Organization.type and is the only
  // signal left when a user has no organizations array at all.
  const kind = resolveOrgKind(
    currentOrganization ?? (user?.user_type ? { type: user.user_type } : null),
  );
  return { role, kind, features, grants, softphoneEntitled };
}
